import React, { useRef, useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  Col, Button, Form, FormControl, InputGroup, FormLabel, Spinner, Row, Modal
} from 'react-bootstrap';
import { Eye, EyeSlash } from 'react-bootstrap-icons';
import StatusAlert from '../components/StatusAlert';
import { emailPattern, namePattern } from '../common/constants';
import useAuth from '../hooks/useAuth';
import AgreementCheckbox from '../components/AgreementCheckbox';
import './signup.css';
import { getConfig } from './../api';


function Signup() {
  const title = 'Signup';
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { addUser } = useAuth();
  const navigate = useNavigate();
  const {
    register,
    watch,
    handleSubmit,
    formState: { errors, isSubmitted },
    reset
  } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange'
  });

  const [enterpriseOrg, setEnterpriseOrg] = useState("");

  useEffect(() => {
    checkEnterprise();
  }, []);

  const checkEnterprise = async () => {
    const response = await getConfig();
    const data = response.data;
    setEnterpriseOrg(data.enterpriseOrg);
  };

  const password = useRef({});
  password.current = watch("password", "");

  const alertOpts = useRef({ isShow: false, message: '' });

  const handleDismiss = () => {
    alertOpts.current.isShow = false;
  };

  const handleClose = () => {
    setShowModal(false);
  };
  const closeAndNavigate = () => {
    handleClose();
    navigate('/login');
  };
  const handleSignup = async (data) => {

    if (Object.keys(errors).length === 0) {
      try {
        setIsLoading(true);
        await addUser(data);
        setIsLoading(false);
        reset(); // Reset form after successful submission
        setShowModal(true);
        //navigate('/login');
      } catch (err) {
        setIsLoading(false);
        alertOpts.current = { isShow: true, message: err.message };
      }
    } else {
      let errorMessage = 'Please fill all required fields correctly.';
      if (errors.agree) {
        errorMessage += ' You must agree to the terms and conditions.';
      }
      alertOpts.current = { isShow: true, message: errorMessage };
    }
    // Force a re-render to show the alert
    //setIsLoading(prev => !prev);
  };

  const phonePattern = /^(\+1|1)?[-.\s]?\(?[2-9]\d{2}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/;
  const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>

      <div className="container-fluid mb-5 px-3">
        <div className="row mx-auto align-content-center" style={{ maxWidth: "50rem", minHeight: "70vh" }}>
          <div className="card card-lg bg-white p-4" >
            <div className="card-body">
              <Form className="row g-2" noValidate onSubmit={handleSubmit(handleSignup)}>
                <div class="mb-4">
                  <h4 className="text-secondary mb-1">Account Creation</h4>
                  <p className="small">Fill up the form and then click <strong>Create Account</strong> button to sign up.</p>
                </div>
                <Row>
                  <Form.Group as={Col} lg="6" controlId="inputFirstName">
                    <FormLabel>First Name</FormLabel>
                    <FormControl type="text"
                      isInvalid={errors.firstname}
                      placeholder="First Name"
                      {...register('firstname', { required: true })}
                    />
                    <Form.Control.Feedback type="invalid">First name is required</Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group as={Col} lg="6" controlId="inputLastName">
                    <FormLabel>Last Name</FormLabel>
                    <FormControl type="text"
                      isInvalid={errors.lastname}
                      placeholder="Last Name"
                      {
                      ...register('lastname', {
                        required: true,
                        pattern: namePattern,
                      })
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.lastname?.type === 'required' && 'Last name is required'}
                      {errors.lastname?.type === 'pattern' && 'No special characters allowed except hyphen'}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Row>
                <Row>
                  <Form.Group as={Col} lg="6" controlId="inputEmail">
                    <FormLabel>Email</FormLabel>
                    <FormControl type="email"
                      isInvalid={errors.email}
                      placeholder="Email@domain.com"
                      {
                      ...register('email', {
                        required: true,
                        pattern: emailPattern,
                      })
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.email?.type === 'required' && 'Email is required'}
                      {errors.email?.type === 'pattern' && 'Invalid email'}
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group as={Col} lg="6" controlId="inputPhone">
                    <FormLabel>Phone</FormLabel>
                    <FormControl type="tel"
                      isInvalid={errors.phone}
                      placeholder="(123) 456-7890"
                      {
                      ...register('phone', {
                        required: true,
                        pattern: phonePattern,
                      })
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.phone?.type === 'required' && 'Phone number is required'}
                      {errors.phone?.type === 'pattern' && 'Invalid phone number format'}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Row>
                <Row>
                  <Form.Group as={Col} md="12" controlId="inputUsername">
                    <Form.Label>Username</Form.Label>
                    <InputGroup hasValidation>
                      <Form.Control type="text"
                        isInvalid={errors.username}
                        placeholder="Username"
                        aria-describedby="inputGroupPrepend"
                        {...register('username', { required: true })}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.username && 'Username is required'}
                      </Form.Control.Feedback>
                    </InputGroup>
                  </Form.Group>
                </Row>
                <Row>
                  <Form.Group as={Col} lg="6" controlId="inputPassword" >
                    <FormLabel>Password</FormLabel>
                    <InputGroup hasValidation>
                      <FormControl
                        type={showPassword ? "text" : "password"}
                        isInvalid={errors.password}
                        placeholder="Password"
                        {
                        ...register('password', {
                          required: true,
                          pattern: passwordPattern,
                        })
                        }
                      />
                      <Button
                        variant="outline-secondary"
                        onClick={() => setShowPassword(!showPassword)}
                        className="password-toggle"
                      >
                        {showPassword ? <EyeSlash /> : <Eye />}
                      </Button>
                      <Form.Control.Feedback type="invalid">
                        {errors.password?.type === 'required' && 'Password is required'}
                        {errors.password?.type === 'pattern' && 'Password must be at least 8 characters long and include at least one uppercase letter, one lowercase letter, one number, and one special character'}
                      </Form.Control.Feedback>
                    </InputGroup>

                  </Form.Group>
                  <Form.Group as={Col} lg="6" controlId="inputConfirmPassword">
                    <FormLabel>Confirm Password</FormLabel>
                    <InputGroup hasValidation>
                      <FormControl
                        type={showConfirmPassword ? "text" : "password"}
                        isInvalid={errors.confirmPassword}
                        placeholder="Confirm Password"
                        {
                        ...register('confirmPassword', {
                          required: true,
                          validate: value =>
                            value === password.current || "The passwords do not match"
                        })
                        }
                      />
                      <Button
                        variant="outline-secondary"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="password-toggle"
                      >
                        {showConfirmPassword ? <EyeSlash /> : <Eye />}
                      </Button>
                      <Form.Control.Feedback type="invalid">
                        {errors.confirmPassword?.type === 'required' && 'Confirm password is required'}
                        {errors.confirmPassword?.type === 'validate' && errors.confirmPassword.message}
                      </Form.Control.Feedback>
                    </InputGroup>

                  </Form.Group>
                </Row>
                <AgreementCheckbox register={register} errors={isSubmitted ? errors : {}} />
                <Row>
                  <Form.Group as={Col} lg="6" >
                    <Button className="btn btn-lg btn-primary mt-3"
                      type="submit"
                      disabled={isLoading}
                    >
                      <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" hidden={!isLoading} />
                      <span className="px-2">Create Account</span>
                    </Button>
                  </Form.Group>
                </Row>
              </Form>

              <StatusAlert show={alertOpts.current.isShow}
                variant="failure"
                message={alertOpts.current.message}
                onDismiss={handleDismiss}
              />
            </div>
          </div>
        </div>
      </div>
      <Modal show={showModal} onHide={handleClose} size="lg">
        <Modal.Header>
          <h3>Account created!</h3>
          <Button variant="close" onClick={handleClose} />
        </Modal.Header>
        <Modal.Body>
          <div className="container-fluid">
            <div className="row gy-2">
              <div className="col-sm-12">
                <div className="card-header pt-0 d-flex flex-row justify-content-between">
                  <div>
                    <h2 className="card-header-title mb-1">Thank you for creating your Account.</h2>
                    <h5 className="fw-light">
                      <p>
                        {enterpriseOrg === ""
                          ? "You've completed registration. You can now log in and access AccessEVA."
                          : "You've completed registration, and it is currently being reviewed. Once approved, we will notify you so you can log in."}
                      </p>

                    </h5>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
        {enterpriseOrg === "" && (
          <>
            <Modal.Footer>
              <Button onClick={closeAndNavigate} variant="primary">
                Login
              </Button>
            </Modal.Footer>
          </>
        )}

      </Modal>


    </>
  );
}

export default Signup;